/**
 * Task #138 — client-side image compression for uploads.
 *
 * Why client-side, not server-side sharp:
 *   We use presigned URLs for object storage uploads, so the file
 *   bypasses the api-server entirely. The server only sees the
 *   metadata at /storage/uploads/request-url. Doing compression in
 *   the browser before requesting the URL means the small compressed
 *   file is what hits the size cap (1.5 MB), and the server never has
 *   to proxy raw image bytes.
 *
 * Side benefit — privacy:
 *   Re-encoding through a 2D canvas naturally strips EXIF metadata
 *   (geolocation, camera serial, capture timestamp). The output is a
 *   fresh JPEG/WebP with only pixel data; no separate exif-strip step
 *   is required.
 *
 * Silent shrink contract (task requirement):
 *   The server is documented as raising a soft warning rather than
 *   rejecting an oversized upload, but the teacher-facing UX is "the
 *   photo just gets smaller — no error". So this routine is required
 *   to GUARANTEE a result that fits the cap whenever the input is a
 *   reasonable raster image. We achieve that by chaining two loops:
 *
 *     1) For each (format, quality) pair at the requested max
 *        dimension, return as soon as we're under the cap.
 *     2) If nothing fits, halve the dimension and try again, down to
 *        a 256-px floor. A 256x256 JPEG at q=0.55 is < 50 KB even for
 *        the noisiest input, so we always converge.
 *
 *   Only non-raster inputs (gif, svg, unreadable file) bypass the
 *   guarantee — those are returned untouched and the caller must
 *   handle them.
 */

export interface CompressOptions {
  /** Max dimension in pixels on the longest edge. Default 1600. */
  maxDimension?: number;
  /** Hard byte cap; we'll try multiple qualities to fit. Default 1.5 MB. */
  maxBytes?: number;
  /** Preferred output mime; falls back to image/jpeg if unsupported. */
  preferredFormat?: "image/webp" | "image/jpeg";
}

const DEFAULT_MAX_DIMENSION = 1600;
const DEFAULT_MAX_BYTES = Math.floor(1.5 * 1024 * 1024);
const MIN_DIMENSION_FLOOR = 256;

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality: number,
): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b), type, quality);
  });
}

async function encodeAtDimension(
  img: HTMLImageElement,
  maxDimension: number,
  formatsToTry: string[],
  qualities: number[],
  maxBytes: number,
): Promise<{ blob: Blob; type: string } | null> {
  const longest = Math.max(img.width, img.height);
  const scale = longest > maxDimension ? maxDimension / longest : 1;
  const targetW = Math.max(1, Math.round(img.width * scale));
  const targetH = Math.max(1, Math.round(img.height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(img, 0, 0, targetW, targetH);

  let best: { blob: Blob; type: string } | null = null;
  for (const type of formatsToTry) {
    for (const q of qualities) {
      const blob = await canvasToBlob(canvas, type, q);
      if (!blob) break; // unsupported format; try next
      if (!best || blob.size < best.blob.size) best = { blob, type };
      if (blob.size <= maxBytes) return { blob, type };
    }
  }
  return best;
}

/**
 * Compress an image file to fit under `maxBytes`. Always returns a
 * fresh `File` (with a `.jpg` or `.webp` extension); never returns the
 * original. If the input isn't a raster image (gif, svg, unreadable),
 * the original is returned unchanged.
 *
 * For raster inputs the result is guaranteed ≤ `maxBytes`: if the
 * default 1600px target doesn't fit, we step down dimension by half
 * until we either fit or hit the 256px floor.
 */
export async function compressImage(
  input: File,
  opts: CompressOptions = {},
): Promise<File> {
  if (!input.type.startsWith("image/")) return input;
  // GIFs would lose animation; SVG is vector. Leave alone.
  if (input.type === "image/gif" || input.type === "image/svg+xml") return input;

  const startDimension = opts.maxDimension ?? DEFAULT_MAX_DIMENSION;
  const maxBytes = opts.maxBytes ?? DEFAULT_MAX_BYTES;
  const preferred = opts.preferredFormat ?? "image/webp";

  let img: HTMLImageElement;
  try {
    img = await loadImage(input);
  } catch {
    return input;
  }

  const formatsToTry: string[] = preferred === "image/webp"
    ? ["image/webp", "image/jpeg"]
    : ["image/jpeg"];
  const qualities = [0.85, 0.7, 0.55];

  // Iterative downscale: try the requested dimension, then halve and
  // retry until we either fit under the cap or hit the floor. This
  // guarantees convergence — a 256x256 JPEG at q=0.55 is well under
  // any reasonable cap.
  let dimension = startDimension;
  let best: { blob: Blob; type: string } | null = null;
  while (dimension >= MIN_DIMENSION_FLOOR) {
    const result = await encodeAtDimension(
      img, dimension, formatsToTry, qualities, maxBytes,
    );
    if (result && result.blob.size <= maxBytes) {
      best = result;
      break;
    }
    if (result && (!best || result.blob.size < best.blob.size)) {
      best = result;
    }
    dimension = Math.floor(dimension / 2);
  }

  if (best) {
    const ext = best.type === "image/webp" ? "webp" : "jpg";
    const baseName = input.name.replace(/\.[^.]+$/, "");
    return new File([best.blob], `${baseName}.${ext}`, { type: best.type });
  }
  return input;
}
